import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { SimilarHotels } from 'components/CarouselWidgets/components/SimilarHotels';
import { CarouselWidgetsProvider } from 'components/CarouselWidgets/context/CarouselWidgetsContext';
import { useNavigateToSearchWithTrigger } from 'hooks/useNavigateToSearch';
import { hideOverlay } from 'modules/overlay/slice';
import { getIsAnchorHotel } from 'modules/sapiSearch/selectors';
import { SearchTrigger } from 'modules/searchBox/types';
import { toggle } from 'opticks';
import { generateAccommodationPageURL } from 'utils/url';
import { Button } from '@daedalus/atlas/Button/Button';
import { useDeviceLayout } from '@daedalus/atlas/context/deviceLayout';
import ContentWrapper from '@daedalus/atlas/helpers/ContentWrapper';
import { PageWidthContainer } from '@daedalus/atlas/helpers/PageWidthContainer';
import { Text } from '@daedalus/atlas/Text';
import { useInView } from '@daedalus/core/src/_web/utils/browser/hooks/useInView';
import { useDispatchTeamEvent, useTrackingTeam } from '@daedalus/core/src/analytics/components/TeamContext';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { Action, Category, Entity } from '@daedalus/core/src/analytics/types/Events';
import { TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY } from '@daedalus/core/src/auth/modules/actions';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { useSuggestionsParameters } from './useSuggestionsParameters';
type SuggestedHotelsProps = {
  hotelId: string;
  onHotelsLoadingComplete: (hasHotels: boolean) => void;
  hasPadding?: boolean;
  hasSideMargin?: boolean;
  hasWhiteBackground?: boolean;
};
const _exp = /*#__PURE__*/() => ContentWrapper;
const TopSection = /*#__PURE__*/styled(_exp())({
  name: "TopSection",
  class: "tbixomz",
  propsAsIs: true
});
const SeeAllText = /*#__PURE__*/styled('span')({
  name: "SeeAllText",
  class: "s6bec34",
  propsAsIs: false
});
const getSearchNavigationOptions = (isMobile: boolean) => isMobile ? {
  mergeWithCurrentParams: false,
  method: 'navigate' as const
} : {
  mergeWithCurrentParams: true,
  method: 'navigate' as const,
  delay: TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY
};
const _exp4 = /*#__PURE__*/() => ContentWrapper;
const SimilarPropertiesWrapper = /*#__PURE__*/styled(_exp4())({
  name: "SimilarPropertiesWrapper",
  class: "su28vbj",
  propsAsIs: true
});
export const AccommodationViewSuggestedHotels = ({
  hotelId,
  onHotelsLoadingComplete,
  hasPadding,
  hasWhiteBackground,
  hasSideMargin = true
}: SuggestedHotelsProps) => {
  const {
    isMobile
  } = useDeviceLayout();
  const {
    anchorHotelId,
    placeId,
    rooms,
    checkIn,
    checkOut
  } = useSuggestionsParameters({
    stable: isMobile
  });
  const {
    ref,
    inView
  } = useInView({
    triggerOnce: true,
    threshold: 0.5
  });
  const dispatchTeamEvent = useDispatchTeamEvent();
  const dispatch = useDispatch();
  const baseSearchParameters = {
    rooms,
    checkIn,
    checkOut,
    userSearch: '1' as const
  };
  const navigationOptions = getSearchNavigationOptions(isMobile);
  const navigateToSearch = useNavigateToSearchWithTrigger(SearchTrigger.AccommodationSuggestedHotels);
  const team = useTrackingTeam();
  const {
    search
  } = useLocation();
  const isAnchorHotel = useSelector(state => getIsAnchorHotel(state, hotelId));
  useEffect(() => {
    if (inView) dispatchTeamEvent(trackEvent({
      category: Category.User,
      entity: Entity.HotelDetailsSimilarHotelsSection,
      action: Action.Viewed
    }));
  }, [dispatchTeamEvent, inView]);
  const onHotelClick = ({
    hotelId: clickedHotelId
  }: {
    hotelId: string;
  }) => {
    const isDesktopAPExperimentBSide = toggle('377fd843-desktop-ap--v2', false, true);
    // Added as part of 377fd843-desktop-ap--v2
    if (isDesktopAPExperimentBSide && !isMobile) {
      setTimeout(() => {
        window.open(generateAccommodationPageURL(clickedHotelId, search, isAnchorHotel), '_blank');
      }, TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY);
      return;
    }
    if (anchorHotelId && anchorHotelId === clickedHotelId) {
      dispatch(hideOverlay());
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 500);
      return;
    }
    navigateToSearch({
      ...baseSearchParameters,
      hotelId: clickedHotelId
    }, navigationOptions);
  };
  const onSeeAllClick = () => {
    dispatchTeamEvent(trackEvent({
      category: Category.User,
      entity: Entity.SeeMoreSimilarProperties,
      action: Action.Clicked,
      payload: {
        ...(hotelId && {
          hotelId
        }),
        ...(placeId && {
          placeId
        })
      }
    }));
    if (isMobile && anchorHotelId && anchorHotelId === hotelId) {
      dispatch(hideOverlay());
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 500);
      return;
    }
    navigateToSearch({
      ...baseSearchParameters,
      hotelId
    }, navigationOptions);
  };
  const trackingPayload = {
    component: 'SimilarHotelAccomodationPage',
    payload: {
      accommodationPageHotelId: hotelId
    },
    team
  };
  return <SimilarPropertiesWrapper className={cx(hasWhiteBackground && '--white-bg')}>
      <PageWidthContainer>
        <ContentWrapper paddingTop="s600" paddingBottom={!isMobile && hasPadding ? 's900' : 's000'}>
          <div ref={ref} data-body-scroll-lock-ignore>
            <TopSection paddingBottom="s500" paddingLeft={hasPadding ? 's500' : 's000'} paddingRight="s200">
              <Text variant="titleL">
                <FormattedMessageWrapper id="suggestion.similarProperties" defaultMessage="Similar properties" />
              </Text>

              <Button variant={isMobile ? 'quiet' : 'secondary'} onClick={onSeeAllClick}>
                <SeeAllText>
                  <FormattedMessageWrapper id="hotelCard.seeAll" defaultMessage="See all" />
                </SeeAllText>
              </Button>
            </TopSection>
            <CarouselWidgetsProvider>
              <SimilarHotels hotelId={hotelId} placeId={placeId} rooms={rooms} checkIn={checkIn} checkOut={checkOut} trackingPayload={trackingPayload} onHotelClick={onHotelClick} onRequestComplete={hotelIds => onHotelsLoadingComplete(hotelIds.length > 0)} hasInsideMargin={hasSideMargin} parentId="accommodation-page" />
            </CarouselWidgetsProvider>
          </div>
        </ContentWrapper>
      </PageWidthContainer>
    </SimilarPropertiesWrapper>;
};

require("./AccommodationViewSuggestedHotels.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./AccommodationViewSuggestedHotels.tsx");